import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { DATE_FORMAT, KeyCode } from './constants';
import { toEventObject, getDateTimeFromDateString } from './helpers';
import Calendar from './Calendar';

DateInput.propTypes = {
  name: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  allowBlank: PropTypes.bool,
};
export default function DateInput({
  name,
  value,
  onChange,
  onFocus = () => {},
  onBlur = () => {},
  disabled = false,
  allowBlank = true,
  ...inputProps
}) {
  const [focused, setFocused] = useState(false);
  const [opened, setOpened] = useState(false);
  const handleFocus = (event) => {
    setFocused(true);
    onFocus(event);
  };
  const handleBlur = (event) => {
    setFocused(false);
    onBlur(event);
  };
  const handleClick = () => setOpened(!opened);
  const handleKeyDown = ({ keyCode }) => {
    if (opened) return;
    if (keyCode !== KeyCode.ENTER) return;

    if (allowBlank && _.isEmpty(value.trim())) {
      lastValidValueRef.current = value;
      return;
    }

    const dateTime = getDateTimeFromDateString(value);
    if (!dateTime.isValid) {
      return void onChange(toEventObject(name, lastValidValueRef.current));
    }

    lastValidValueRef.current = dateTime.toFormat(DATE_FORMAT);
    if (lastValidValueRef.current === value) return;

    onChange(toEventObject(name, lastValidValueRef.current));
  };

  const lastValidValueRef = useRef(value);
  useEffect(() => {
    if (focused) return;

    if (allowBlank && _.isEmpty(value.trim())) {
      lastValidValueRef.current = value;
      return;
    }

    const dateTime = getDateTimeFromDateString(value);
    if (!dateTime.isValid) {
      return void onChange(toEventObject(name, lastValidValueRef.current));
    }

    lastValidValueRef.current = dateTime.toFormat(DATE_FORMAT);
    if (lastValidValueRef.current === value) return;

    onChange(toEventObject(name, lastValidValueRef.current));
  }, [focused]);

  const elemRef = useRef();
  useEffect(() => {
    if (!opened) return;

    const onClickBody = ({ target }) => {
      if (elemRef.current.contains(target)) return;
      setOpened(false);
    };
    document.body.addEventListener('click', onClickBody);
    return () => {
      document.body.removeEventListener('click', onClickBody);
    };
  }, [opened]);

  const handleChangeCalendarDate = (value) => {
    lastValidValueRef.current = value;
    setOpened(false);
    onChange(toEventObject(name, value));
  };

  return (
    <div ref={elemRef} className={classNames('time_picker', { on: opened })}>
      <input
        {...inputProps}
        type="text"
        className={classNames({ disabled })}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {opened && (
        <div className="ly_calendar">
          <Calendar date={value} onChangeDate={handleChangeCalendarDate} />
        </div>
      )}
    </div>
  );
}
DateInput.getNowDateString = getNowDateString;

export function getNowDateString() {
  return DateTime.local().toFormat(DATE_FORMAT);
}
